import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { firstValueFrom, filter, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { BsModalService } from 'ngx-bootstrap/modal';

import { VAConversationRow } from './dashboard-table.interface';
import { RESOLUTION_TYPES } from 'src/app/modules/voice-ai/resolution-types';
import { AuthService } from 'src/app/services/auth.service';
import { ApiGatewayAnalyticsService } from 'src/app/services/api-gateway-analytics/api-gateway-analytics.service';
import { ConversationService } from 'src/app/modules/voice-ai-conversation/conversation.service';
import { HumanInLoopService } from 'src/app/services/firestore';
import { HlConversationAssignUserModalComponent } from 'src/app/components/human-in-the-loop/hl-conversation-assign-user-modal/hl-conversation-assign-user-modal.component';
import { UserModel } from 'src/app/models';
import { ClientEnvironmentService } from 'src/app/services/client-environment.service';
import { DumbledoreService } from 'src/app/services/dumbledore.service';

@Component({
  selector: 'app-dashboard-table',
  templateUrl: './dashboard-table.component.html',
  styleUrls: ['./dashboard-table.component.scss'],
})
export class DashboardTableComponent implements OnInit {
  readonly VALID_WORKLISTS = [
    { id: 'unresolved', label: 'Unresolved' },
    { id: 'urgent', label: 'Urgent' },
    { id: 'assignedToMe', label: 'Assigned to Me' },
    { id: 'inProgress', label: 'In Progress' },
    { id: 'resolved', label: 'Resolved' },
  ] as const;

  @Input() corpId: string;
  @Input() conversationRows: VAConversationRow[] = [];
  @Input() intentsForFilter: { code: string; name: string }[] = [];
  @Input() totalCount: number = 0;
  @Input() workListCounts: Record<string, number> = {};
  @Input() currentPage: number = 1;
  @Input() searchTerm: string = '';
  @Input() selectedIntent: string = '';
  @Input() selectedWorklist: string = 'unresolved';
  @Input() selectedAssignedTo: string = '';
  @Input() sortBy: string = 'date';
  @Input() sortOrder: 'asc' | 'desc' = 'desc';
  @Output() filtersChanged = new EventEmitter<{
    searchTerm: string;
    intent: string;
    worklist: string;
    assignedTo: string;
    page: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>();

  @Output() conversationUpdated = new EventEmitter<{ conversation: VAConversationRow }>();

  resolutionTypes = RESOLUTION_TYPES;
  itemsPerPage: number = 25;
  showResolveSubmenu = false;
  currentUser: UserModel | null = null;
  agentName: string;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private authService: AuthService,
    private apiGatewayAnalyticsService: ApiGatewayAnalyticsService,
    private conversationService: ConversationService,
    public humanInLoopService: HumanInLoopService,
    private clientEnvironmentService: ClientEnvironmentService,
    private modalService: BsModalService,
    private toasterService: ToastrService,
    private dumbledoreService: DumbledoreService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.currentUser = await this.authService.getCurrentUserProfile();
    this.agentName = `${this.currentUser?.firstName ?? ''} ${this.currentUser?.lastName ?? ''}`.trim();
    // Pass false to preserve currentPage from @Input (e.g., from URL query params)
    // This ensures back navigation maintains the correct page
    this.applyFilters(false);
  }

  toggleResolveSubmenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation(); // Prevent the parent menu from closing
    this.showResolveSubmenu = !this.showResolveSubmenu;
  }

  onMenuOpenChange(item: VAConversationRow, isOpen: boolean): void {
    item.isMenuOpen = isOpen;
    if (!isOpen) {
      this.showResolveSubmenu = false;
    }
  }

  applyFilters(resetPage: boolean = true): void {
    if (resetPage) {
      this.currentPage = 1;
    }
    this.emitFilters();
  }

  emitFilters(): void {
    this.filtersChanged.emit({
      searchTerm: this.searchTerm,
      intent: this.selectedIntent,
      worklist: this.selectedWorklist,
      assignedTo: this.selectedAssignedTo,
      page: this.currentPage,
      ...(this.sortBy ? { sortBy: this.sortBy } : {}),
      ...(this.sortOrder ? { sortOrder: this.sortOrder } : {}),
    });
  }

  changePage(page: number): void {
    if (page === this.currentPage) return;
    this.currentPage = page;
    this.emitFilters();
  }

  setWorklist(type: string): void {
    this.selectedAssignedTo = '';
    this.selectedWorklist = type;
    this.applyFilters();
  }

  getFormattedTime(minutes: number): string {
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;
    let result = '';
    if (days > 0) result += `${days}d `;
    if (hours > 0) result += `${hours}h `;
    result += `${mins}min`;
    return result.trim();
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.itemsPerPage);
  }

  get visiblePages(): number[] {
    const maxPagesToShow = 5;
    const pages: number[] = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(1, this.currentPage - half);
    let end = Math.min(this.totalPages, start + maxPagesToShow - 1);

    if (end - start < maxPagesToShow - 1) {
      start = Math.max(1, end - maxPagesToShow + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  // search should apply filters after a delay to avoid too many requests
  searchText(): void {
    clearTimeout(this.searchTimeout!);
    this.searchTimeout = setTimeout(() => {
      this.applyFilters();
    }, 600);
  }

  public async resolve(conversation: VAConversationRow, resolutionCode: (typeof RESOLUTION_TYPES)[0]['id']) {
    try {
      await this.dumbledoreService.setConversationResolution(conversation.db_conversation_id, resolutionCode);
      conversation.resolution = resolutionCode;
      this.toasterService.success(resolutionCode ? 'Issue has been resolved.' : 'Issue has been reopened.');

      this.conversationUpdated.emit({ conversation });
    } catch (error) {
      this.toasterService.error('Error updating resolution for conversation.');
    }
  }

  public async assignTo(row: VAConversationRow): Promise<void> {
    try {
      const reducedBotCode = await this.getBotCodeFromBotFullId(row.bot_id);

      const users = await this.humanInLoopService.getBotUsers(this.corpId, reducedBotCode, this.currentUser);

      let assignedUsersIds: string[] = [];
      if (row.db_conversation_id) {
        const conversation = await this.conversationService.getConversationByIdSnapshot(this.corpId, row.db_conversation_id);
        assignedUsersIds = (conversation.assignments ?? []).map(agent => agent.agentId);
      }

      const modalRef = this.modalService.show(HlConversationAssignUserModalComponent, {
        initialState: {
          users,
          assignedUsers: users.filter(user => assignedUsersIds.includes(user.id)),
        },
      });

      modalRef.content.save.pipe(take(1)).subscribe(async (user: UserModel) => {
        modalRef.hide();
        const assignedBy = this.currentUser ? `${this.currentUser.firstName} ${this.currentUser.lastName}` : '';
        this.assignConversationToUser(row, user, assignedBy);
      });

      modalRef.content.removeUser.pipe(take(1)).subscribe(async (user: UserModel) => {
        modalRef.hide();
        const assignedBy = this.currentUser ? `${this.currentUser.firstName} ${this.currentUser.lastName}` : '';
        this.removeUser(row, user, assignedBy);
      });
    } catch (error) {
      this.toasterService.error('Could not open assignment modal.');
    }
  }

  public async assignToMe(row: VAConversationRow): Promise<void> {
    if (!this.currentUser) {
      this.toasterService.error('You must be logged in to assign conversations.');
      return;
    }
    const assignedBy = `${this.currentUser.firstName} ${this.currentUser.lastName}`;
    await this.assignConversationToUser(row, this.currentUser, assignedBy);
  }

  private async assignConversationToUser(row: VAConversationRow, user: UserModel, assignedBy: string) {
    try {
      const botId = row.bot_id;
      await this.humanInLoopService.addAgentToConversation(botId.split('-')[0], user, row.db_conversation_id);

      await this.humanInLoopService.touchConversation(this.corpId, row.db_conversation_id);

      try {
        await this.apiGatewayAnalyticsService.setConversationAgent(row.db_conversation_id, `${user.firstName} ${user.lastName}`);
      } catch (error) {
        // Analytics error is non-critical, conversation assignment still succeeded
      }

      this.conversationUpdated.emit({ conversation: row });
      this.toasterService.success('Conversation assigned successfully.');
    } catch (error) {
      this.toasterService.error('Could not assign conversation to user.');
    }
  }

  private async removeUser(row: VAConversationRow, user: UserModel, assignedBy: string) {
    try {
      const botId = row.bot_id;
      const conversationId = row.db_conversation_id;

      if (!conversationId) {
        throw new Error('No conversationId found');
      }

      await this.humanInLoopService.removerAgentFromConversation(botId.split('-')[0], user, conversationId);

      try {
        await this.apiGatewayAnalyticsService.setConversationAgent(conversationId, '');
      } catch (error) {
        // Analytics error is non-critical, agent removal still succeeded
      }

      this.conversationUpdated.emit({ conversation: row });
      this.toasterService.success('Agent removed from conversation.');
    } catch (error) {
      this.toasterService.error('Could not remove agent from conversation.');
    }
  }

  private async getBotCodeFromBotFullId(botFullId: string) {
    const envs = await firstValueFrom(this.clientEnvironmentService.items$.pipe(filter(envs => envs !== null)));

    const parts = botFullId.split('-');
    let botCode = parts[parts.length - 1];
    if (envs && envs.stages) {
      const env = envs.stages.filter(e => {
        const suffix = `_${e.systemName}`;
        return botCode.endsWith(suffix);
      });

      if (env.length > 0) {
        const suffix = `_${env[0].systemName}`;
        botCode = botCode.slice(0, botCode.length - suffix.length);
      }
    }
    return botCode;
  }

  onSort(column: string): void {
    if (this.sortBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortOrder = 'asc';
    }
    this.applyFilters();
  }

  getSortIcon(column: string): 'fas fa-long-arrow-alt-up' | 'fas fa-long-arrow-alt-down' {
    return this.sortOrder === 'asc' ? 'fas fa-long-arrow-alt-up' : 'fas fa-long-arrow-alt-down';
  }

  get workListHasAnyCount(): boolean {
    return Object.keys(this.workListCounts).length > 0;
  }

  trackByConversationId(_index: number, conversation: VAConversationRow): string {
    return conversation.db_conversation_id;
  }

  trackByIntentCode(_index: number, intent: { code: string; name: string }): string {
    return intent.code;
  }

  trackByPageNumber(_index: number, page: number): number {
    return page;
  }

  trackByResolutionId(_index: number, type: { id: string; name: string }): string {
    return type.id;
  }
}
